import { createTheme, type ThemeOptions } from '@mui/material'
import { tokens } from './tokens'

const typography: ThemeOptions['typography'] = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontWeight: 700, letterSpacing: '-0.025em' },
  h2: { fontWeight: 700, letterSpacing: '-0.025em' },
  h3: { fontWeight: 700, letterSpacing: '-0.02em' },
  h4: { fontWeight: 700, letterSpacing: '-0.02em' },
  h5: { fontWeight: 600, letterSpacing: '-0.01em' },
  h6: { fontWeight: 600 },
  body1: { fontSize: '0.9375rem' },
  body2: { fontSize: '0.8125rem' },
  caption: { fontSize: '0.75rem', letterSpacing: '0.01em' },
  button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
}

export const surface = {
  muted: '#F3F4F6',
  subtle: '#F9FAFB',
  accent: '#E0EAFB',
} as const

const palette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: { main: '#5B8DEF', light: '#A9C5FA', dark: '#3A6FD0', contrastText: '#FFFFFF' },
  secondary: { main: '#7C5CFC' },
  success: { main: '#22C55E', light: '#86EFAC', dark: '#15803D' },
  warning: { main: '#F59E0B', light: '#FCD34D', dark: '#B45309' },
  error: { main: '#EF4444', light: '#FCA5A5', dark: '#B91C1C' },
  info: { main: '#60A5FA' },
  background: { default: '#EDEEF1', paper: '#FFFFFF' },
  text: { primary: '#0F172A', secondary: '#6B7280' },
  divider: 'rgba(15, 23, 42, 0.06)',
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
}

export const theme = createTheme({
  palette,
  typography,
  shape: { borderRadius: tokens.borderRadius },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingInline: 18,
          paddingBlock: 8,
          fontWeight: 600,
        },
        containedPrimary: ({ theme }) => ({
          boxShadow: 'none',
          backgroundColor: theme.palette.primary.main,
          '&:hover': { boxShadow: tokens.shadow.soft },
        }),
      },
      defaultProps: { disableElevation: true },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: tokens.borderRadius,
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
      defaultProps: { elevation: 0 },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRight: 'none',
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
        }),
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', fullWidth: true, size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          color: theme.palette.text.secondary,
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            fontWeight: 600,
            '& .MuiListItemIcon-root': { color: theme.palette.text.primary },
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 8, fontSize: 12 } },
    },
  },
})
